import { Link } from '@tanstack/react-router'
import { useDashboardOverview } from '@/hooks/useAnalytics'
import { useBetaPendingCount } from '@/hooks/useBetaRequests'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { QueryError } from '@/components/QueryError'
import {
  Users,
  DollarSign,
  Activity,
  TrendingUp,
  UserCheck,
  TrendingDown,
  Crown,
  ClipboardList,
} from 'lucide-react'

export const DashboardPage = () => {
  const { data: overview, isLoading, isError, isFetching, refetch } = useDashboardOverview()
  const { data: betaCount } = useBetaPendingCount()

  // Find the top plan by user count
  const topPlan =
    overview?.active_subscriptions && Object.entries(overview.active_subscriptions).length > 0
      ? Object.entries(overview.active_subscriptions).sort(([, a], [, b]) => b - a)[0]
      : null

  const stats = [
    {
      title: 'Usuarios Totales',
      value: overview?.total_users || 0,
      icon: Users,
      change: `+${overview?.new_users || 0} este mes`,
    },
    {
      title: 'Usuarios Activos 30d',
      value: overview?.active_users || 0,
      icon: UserCheck,
      change: overview?.total_users
        ? `${Math.round(((overview.active_users || 0) / overview.total_users) * 100)}% del total`
        : '-',
    },
    {
      title: 'MRR',
      value: `$${(overview?.mrr || 0).toLocaleString()}`,
      icon: DollarSign,
      change: 'Ingresos recurrentes',
    },
    {
      title: 'Ingresos 30d',
      value: `$${(overview?.total_revenue || 0).toLocaleString()}`,
      icon: TrendingUp,
      change: 'Ultimos 30 dias',
    },
    {
      title: 'Docs Consumidos',
      value: (overview?.docs_consumed || 0).toLocaleString(),
      icon: Activity,
      change: `Prom: ${Math.round(overview?.avg_docs_per_user || 0)} por usuario`,
    },
    {
      title: 'Churn Rate',
      value: `${(overview?.churn_rate || 0).toFixed(1)}%`,
      icon: TrendingDown,
      change: 'Ultimos 30 dias',
      variant: (overview?.churn_rate || 0) > 5 ? 'destructive' : 'default',
    },
    {
      title: 'Plan Top',
      value: topPlan ? topPlan[0] : '-',
      icon: Crown,
      change: topPlan ? `${topPlan[1]} usuarios` : 'Sin datos',
      capitalize: true,
    },
    {
      title: 'Solicitudes Beta',
      value: betaCount?.count ?? 0,
      icon: ClipboardList,
      change: 'Pendientes de revision',
      link: '/beta-requests',
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Dashboard</h1>

      {isError && (
        <QueryError
          message="No se pudieron cargar las métricas del dashboard."
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, i) => {
          const content = (
            <Card key={i} className={stat.link ? 'transition-colors hover:border-primary/50' : ''}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <stat.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <>
                    <div
                      className={`text-2xl font-bold ${stat.capitalize ? 'capitalize' : ''} ${stat.variant === 'destructive' ? 'text-destructive' : ''}`}
                    >
                      {stat.value}
                    </div>
                    <p className="text-xs text-muted-foreground">{stat.change}</p>
                  </>
                )}
              </CardContent>
            </Card>
          )
          return stat.link ? (
            <Link key={i} to={stat.link} className="block">
              {content}
            </Link>
          ) : (
            <div key={i}>{content}</div>
          )
        })}
      </div>

      {/* Plan distribution */}
      {overview?.active_subscriptions && Object.keys(overview.active_subscriptions).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribucion por Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(overview.active_subscriptions)
                .sort(([, a], [, b]) => b - a)
                .map(([plan, count]) => (
                  <div key={plan} className="flex items-center gap-2 rounded-lg border px-3 py-2">
                    <span className="text-sm font-medium capitalize">{plan}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
